import { clsx } from "clsx";
import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button, Tooltip } from "@fronterahealth/frontera-ui-components";

import { FormSavingIndicator } from "@components/ReportField/FormSavingIndicator";
import { useHasUnsavedFields } from "@components/ReportField/useHasUnsavedFields";
import { ReassessmentApprovalProgressIndicator } from "@pages/ClinicalReportDetails/ReportPage/Components/ReassessmentApprovalProgressIndicator";
import { useNavigateToReview } from "@pages/ClinicalReportDetails/ReportPage/hooks/useNavigateToReview";
import { useReassessmentApprovalProgress } from "@pages/ClinicalReportDetails/ReportPage/hooks/useReassessmentApprovalProgress";
import { useClinicalReportDetailsData } from "@providers/ClinicalReportDetailsProvider";
import { ReportNode } from "@providers/ReportProvider";

type StepKey = "programming" | "narrative" | "review-and-sign";

const STEP_DEFINITIONS: { key: StepKey; name: string }[] = [
  { key: "programming", name: "Goal Progress" },
  { key: "narrative", name: "Narrative" },
  { key: "review-and-sign", name: "Review" },
];

interface ReassessmentStepProgressNavProps {
  report: ReportNode;
}

/**
 * 3-step wayfinding for the Reassessment review flow, designed to splice into
 * the existing report header (not a separate row). Renders two pieces via a
 * Fragment:
 *   1. An absolutely-centered numbered stepper (1. Goal Progress · 2.
 *      Narrative · 3. Review). The header container is already `relative`
 *      so the absolute element is anchored there.
 *   2. Inline-flow Previous Step / Next Step / Complete buttons that sit on
 *      the right side of the header.
 *
 * Forward gating: advancing TO Review requires 100% goal+target approval.
 * Goal Progress ↔ Narrative move freely in either direction. Stepper items
 * are not the gating affordance — they're silent no-ops when gated; the
 * tooltip lives only on the disabled Next Step button.
 *
 * The Step-2-to-Step-3 transition uses useNavigateToReview so GAS tables get
 * filled before landing on Review, matching the prior Continue-to-review flow.
 */
export const ReassessmentStepProgressNav: React.FC<ReassessmentStepProgressNavProps> = ({ report }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const progress = useReassessmentApprovalProgress(report.id);
  const { isAllApproved, totalGoals, totalTargets, totalNarrativeFields } = progress;
  const hasTrackedProgress = totalGoals + totalTargets + totalNarrativeFields > 0;
  const { navigateToReview, isNavigating } = useNavigateToReview(report);
  const { stepHeaderCompleteAction } = useClinicalReportDetailsData();
  const hasUnsavedFields = useHasUnsavedFields();

  const currentStepIndex = useMemo(() => {
    if (pathname.endsWith("/programming")) return 0;
    if (pathname.endsWith("/narrative")) return 1;
    if (pathname.endsWith("/review-and-sign")) return 2;
    return 0;
  }, [pathname]);

  const goToStep = (index: number) => {
    if (index === 2) {
      navigateToReview();
      return;
    }
    navigate(`/clinical-reports/reassessment/${report.id}/${STEP_DEFINITIONS[index].key}`);
  };

  const isOnReview = currentStepIndex === STEP_DEFINITIONS.length - 1;
  // The approval gate now sits between Narrative and Review (not between
  // Goal Progress and Narrative). Direct jumps to Review from any earlier
  // step are also gated.
  const reviewGateActive = !isAllApproved;

  // On Review, the primary button becomes "Complete" and drives the action
  // published by ReviewAndSign via context. Elsewhere it's "Next Step",
  // disabled on Narrative until approval is complete. Either button is also
  // disabled, without a tooltip, while a field is saving.
  const completeAvailable = isOnReview && !!stepHeaderCompleteAction;
  const nextLabel = isOnReview ? "Complete" : "Next Step";
  // Next Step gates only when the next step IS Review and approval isn't
  // done. From Goal Progress the next step is Narrative — always allowed.
  const nextLeadsToReview = currentStepIndex + 1 === 2;
  const nextDisabled = completeAvailable ? stepHeaderCompleteAction.disabled : nextLeadsToReview && reviewGateActive;
  const nextTooltip = completeAvailable
    ? stepHeaderCompleteAction.disabledTooltip
    : "Approve all goals and finalize the narrative to continue";
  const handleNext = () => {
    if (completeAvailable) {
      void stepHeaderCompleteAction.onComplete();
      return;
    }
    goToStep(currentStepIndex + 1);
  };

  const nextButton = (
    <Button
      appearance="primary"
      text={nextLabel}
      disabled={nextDisabled || isNavigating || hasUnsavedFields}
      onClick={handleNext}
    />
  );

  return (
    <>
      <ol className="absolute left-1/2 flex -translate-x-1/2 items-center gap-4">
        {STEP_DEFINITIONS.map((def, index) => {
          const isCurrent = index === currentStepIndex;
          const isBefore = index < currentStepIndex;
          // Only the Review item gates forward (until approval, or while a
          // field is saving) — Goal Progress and Narrative are reachable from
          // anywhere. Clicks on a gated step item are dropped silently; the
          // approval tooltip lives on the Next Step button so users see "why"
          // in one consistent spot.
          const isForwardGated = !isCurrent && !isBefore && index === 2 && (reviewGateActive || hasUnsavedFields);
          const isClickable = !isCurrent && !isForwardGated;

          return (
            <React.Fragment key={def.key}>
              <li>
                <button
                  type="button"
                  onClick={() => isClickable && goToStep(index)}
                  disabled={!isClickable}
                  className={clsx(
                    "rounded text-xs leading-6 focus:outline-hidden",
                    isCurrent
                      ? "cursor-default font-semibold text-limestone-950"
                      : isForwardGated
                        ? "cursor-not-allowed font-medium text-limestone-700"
                        : "cursor-pointer font-medium text-limestone-700",
                  )}
                >
                  {index + 1}. {def.name}
                </button>
              </li>
              {index < STEP_DEFINITIONS.length - 1 && <div aria-hidden="true" className="h-px w-2 bg-limestone-400" />}
            </React.Fragment>
          );
        })}
      </ol>

      <div className="flex items-center gap-2">
        <FormSavingIndicator />
        {hasTrackedProgress && (
          <>
            <ReassessmentApprovalProgressIndicator
              approvedGoals={progress.approvedGoals}
              totalGoals={progress.totalGoals}
              approvedTargets={progress.approvedTargets}
              totalTargets={progress.totalTargets}
              completedNarrativeFields={progress.completedNarrativeFields}
              totalNarrativeFields={progress.totalNarrativeFields}
              incompleteNarrativeFields={pathname.endsWith("/narrative") ? progress.incompleteNarrativeFields : []}
              percentage={progress.percentage}
            />
            <div aria-hidden="true" className="h-6 w-px bg-limestone-200" />
          </>
        )}
        {currentStepIndex > 0 && (
          <Button
            appearance="secondary"
            text="Previous Step"
            disabled={isNavigating}
            onClick={() => goToStep(currentStepIndex - 1)}
          />
        )}
        {nextDisabled && nextTooltip ? (
          // !max-w-none overrides the Tooltip's default max-w-80 so this
          // single-sentence copy doesn't wrap and leave dead space on the
          // right of the shorter wrapped line.
          <Tooltip showInfoIcon={false} placement="bottom" text={nextTooltip} className="!max-w-none">
            {nextButton}
          </Tooltip>
        ) : (
          nextButton
        )}
      </div>
    </>
  );
};
